import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {GridCellParams, GridColDef} from '@mui/x-data-grid-pro';
import {HistoryOutlined} from '@mui/icons-material';
import EditorGrid from '../../../components/EditorGrid/EditorGrid';
import {useAccountContext} from '../../../contexts/AccountContext';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {ehonsRecommendsColumns} from './recommendsColumns';
import {recommendsGridStyles} from './styles';
import {sppDAColumns} from './sppRecommendsColumns';
import {useOVRecommends} from '../../../hooks/useOVRecommends';
import {
  BusinessUnitEnum,
  GlobalPermissionEnum,
  OVModelsEnum,
} from '../../../schema-types/globalTypes';
import HistoryDiffDialog from '../../../components/AccountReviewDialog/HistoryDiffDialog';
import {HistoryQueryType} from '../../../components/AccountReviewDialog/useHistoryDiff';
import {hasPermission} from '../../../contexts/FeatureBoundary';
import {SaltusAny} from '../../../utils/SaltusAny';
import {ComprehensiveRecsSummary} from './ComprehensiveRecsSummary';
import {
  OVRecommends_ovrecommends,
  OVRecommendsVariables,
} from '../../../schema-types/OVRecommends';
import {StickyDataGrid} from '../../../components/Common/StickyDataGrid';
import useDjango from '../../../hooks/useDjango';

type RecommendsGridProps = Omit<OVRecommendsVariables, 'entityName'> & {
  onSelected?: (args: SaltusAny) => SaltusAny;
  readOnly?: boolean;
  minHeight?: number;
  showOnlyOtherReviews?: boolean;
};

export const RecommendsGrid: React.FC<RecommendsGridProps> = ({
  onSelected,
  readOnly,
  minHeight,
  showOnlyOtherReviews = false,
}) => {
  const {accountType} = useAccountContext();
  const {reviewId} = useReviewContext();
  const {data, error, loading} = useOVRecommends(showOnlyOtherReviews);
  const classes = recommendsGridStyles();
  const navigate = useNavigate();
  const recommends =
    data?.ovrecommends?.filter((r) => {
      if (!r?.review?.id) {
        return false;
      }
      const inThisReview = r.review.id === reviewId;
      if (showOnlyOtherReviews) {
        return !inThisReview;
      }
      return inThisReview;
    }) || [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const handleDialogOpen = (id: string) => {
    setSelectedReviewId(id);
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setSelectedReviewId('');
    setDialogOpen(false);
  };

  const {djangoUser} = useDjango();

  const recommendsHistoryColumn = {
    field: 'history',
    headerName: 'History',
    flex: 1,
    editable: false,
    renderCell: (params: GridCellParams) => {
      return (
        <HistoryOutlined
          style={{cursor: 'pointer'}}
          data-testid="history-review"
          onClick={() => handleDialogOpen(params.id.toString())}
        />
      );
    },
  };

  const ehonsRecommendsColumnsWithHistory = [
    ...ehonsRecommendsColumns.slice(0, -1),
    ...(hasPermission(djangoUser, [GlobalPermissionEnum.can_access_admin_features])
      ? [recommendsHistoryColumn]
      : []),
    ...ehonsRecommendsColumns.slice(-1),
  ];

  return (
    <div
      style={{
        height: '100%',
        flexGrow: 1,
        position: 'relative',
      }}
    >
      {readOnly ? (
        <StickyDataGrid
          sx={classes.dataGridRoot}
          columns={
            (accountType === BusinessUnitEnum.DA
              ? sppDAColumns
              : ehonsRecommendsColumnsWithHistory) as GridColDef[]
          }
          rows={loading ? [] : recommends}
          hideFooter
          sortModel={[{field: 'dateCreated', sort: 'desc'}]}
          loading={loading}
          getDetailPanelHeight={({row}) => 'auto'}
          getDetailPanelContent={({row}) => (
            <ComprehensiveRecsSummary row={row as OVRecommends_ovrecommends} />
          )}
          onCellDoubleClick={(params) => {
            navigate({
              pathname: `../recommends-builder`,
              search: `ovrecommendId=${params.id}`,
            });
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
          }}
        />
      ) : (
        <EditorGrid
          dataGridId="RecommendsGrid"
          sx={classes.dataGridRoot}
          onCellDoubleClick={(params) => {
            // maybe this will fix it for paul
            navigate({
              pathname: `../recommends-builder`,
              search: `ovrecommendId=${params.id}`,
            });
          }}
          columns={
            accountType === BusinessUnitEnum.DA
              ? sppDAColumns
              : ehonsRecommendsColumnsWithHistory
          }
          rows={loading ? [] : recommends}
          hideFooter
          error={error}
          sortModel={[{field: 'dateCreated', sort: 'desc'}]}
          loading={loading}
          onSelected={onSelected}
          minHeight={minHeight ?? '600px'}
          createFunc={false}
          createFuncLoading={false}
          syncFunc={() => {}}
          updateFunc={false}
        />
      )}
      <HistoryDiffDialog
        isOpen={dialogOpen}
        handleClose={handleDialogClose}
        entityId={selectedReviewId}
        entityType={OVModelsEnum.OVRECOMMEND}
        queryType={HistoryQueryType.GENERIC}
      />
    </div>
  );
};
